import type { MediaLinkDocType } from '@ast-data/shared';
import { joinNumbers, splitNumbers } from './descriptionValidation';
import { planOwnerRemoval, type MediaMove } from '../media/mediaLinks';

/**
 * What deleting a Befund or one of its descriptions takes with it.
 *
 * A deleted record used to leave every reference to it standing: a chip in
 * "Im Profil mit" that could only show the raw id, a Foto still pointing at a
 * Planum that no longer exists. The plan names every record that points at what
 * goes, and the patch that takes the reference out, so the confirm dialog can
 * show it before anything is written.
 *
 * Pure: the caller loads the family's records and writes the result.
 */

export type DeletionTarget =
    | { kind: 'finding'; id: string }
    | { kind: 'planum'; id: string }
    | { kind: 'profile'; id: string };

/** The six description relations stored as id arrays. */
const DESCRIPTION_REF_FIELDS = [
    'identicalWithPlanumIds',
    'identicalWithProfileIds',
    'partOfPlanumIds',
    'partOfProfileIds',
    'inProfileWithPlanumIds',
    'inProfileWithProfileIds'
] as const;

/** The stratigraphy fields holding bare Befundnummern. */
const NUMBER_REF_FIELDS = [
    'overliesOrCuts',
    'underliesOrIsCut',
    'contemporaryWith',
    'fills',
    'filledBy'
] as const;

/** The Befund pickers on Fund, Probe, Foto, Zeichenblatt and Gesamtbeschreibung. */
const RECORD_REF_FIELDS = ['planumIds', 'profileIds', 'findingIds'] as const;

export interface PlanFinding {
    id: string;
    findingNumber: string;
}

export interface PlanDescription {
    id: string;
    projectId: string;
    findingId: string;
    identicalWithPlanumIds: string[];
    identicalWithProfileIds: string[];
    partOfPlanumIds: string[];
    partOfProfileIds: string[];
    inProfileWithPlanumIds: string[];
    inProfileWithProfileIds: string[];
    overliesOrCuts: string;
    underliesOrIsCut: string;
    contemporaryWith: string;
    fills: string;
    filledBy: string;
}

export interface PlanRecord {
    id: string;
    projectId: string;
    planumIds: string[];
    profileIds: string[];
    findingIds: string[];
}

/** A picture attached to a Befund or one of its descriptions. */
export interface PlanMedia {
    id: string;
    ownerType: string;
    ownerId: string;
    /** `photo` or `sketch`. */
    kind: string;
}

export type RecordType =
    'find' | 'sample' | 'photo' | 'drawingSheet' | 'overallDescription';

export type ReferencingType = 'planum' | 'profile' | RecordType;

/** The live records of the Teilmaßnahme family, as far as the plan reads them. */
export interface DeletionData {
    findings: PlanFinding[];
    planums: PlanDescription[];
    profiles: PlanDescription[];
    records: Record<RecordType, PlanRecord[]>;
    media: PlanMedia[];
    /** Every link showing a picture in a second description. */
    mediaLinks: MediaLinkDocType[];
}

export interface ReferenceRemoval {
    type: ReferencingType;
    id: string;
    projectId: string;
    /** The stored fields that change, e.g. `inProfileWithPlanumIds`. */
    fields: string[];
    patch: Record<string, unknown>;
}

export interface DeletionPlan {
    /** The Befund itself, when a whole Befund goes. */
    findingIds: string[];
    /** Every description that goes: the target, or all of the Befund's. */
    planumIds: string[];
    profileIds: string[];
    /** The pictures that go: attached to something that goes, linked nowhere else. */
    media: PlanMedia[];
    /** Pictures whose origin goes but which live on where they are linked. */
    mediaMoves: MediaMove[];
    /** Links that go: into anything that goes, plus each move's own. */
    mediaLinkIds: string[];
    removals: ReferenceRemoval[];
}

const RECORD_TYPES: RecordType[] = [
    'find',
    'sample',
    'photo',
    'drawingSheet',
    'overallDescription'
];

function normalizedNumber(number: string): string {
    return number.trim().toLowerCase();
}

/**
 * The Befundnummern that stop existing: the deleted Befunde's, unless another
 * live Befund in the family still carries the same number (a duplicate). A
 * stratigraphy entry names a NUMBER, so it stays as long as that number does.
 */
function vanishingNumbers(
    findings: PlanFinding[],
    deletedFindingIds: Set<string>
): Set<string> {
    const remaining = new Set(
        findings
            .filter((finding) => !deletedFindingIds.has(finding.id))
            .map((finding) => normalizedNumber(finding.findingNumber))
    );
    const vanishing = new Set<string>();
    for (const finding of findings) {
        const number = normalizedNumber(finding.findingNumber);
        if (
            deletedFindingIds.has(finding.id) &&
            number &&
            !remaining.has(number)
        ) {
            vanishing.add(number);
        }
    }
    return vanishing;
}

function withoutIds(ids: string[], removed: Set<string>): string[] | null {
    const kept = ids.filter((id) => !removed.has(id));
    return kept.length === ids.length ? null : kept;
}

function descriptionPatch(
    description: PlanDescription,
    removedPlanumIds: Set<string>,
    removedProfileIds: Set<string>,
    removedNumbers: Set<string>
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const field of DESCRIPTION_REF_FIELDS) {
        const removed = field.endsWith('PlanumIds')
            ? removedPlanumIds
            : removedProfileIds;
        const kept = withoutIds(description[field] ?? [], removed);
        if (kept) {
            patch[field] = kept;
        }
    }
    if (removedNumbers.size > 0) {
        for (const field of NUMBER_REF_FIELDS) {
            const numbers = splitNumbers(description[field]);
            const kept = numbers.filter(
                (number) => !removedNumbers.has(normalizedNumber(number))
            );
            if (kept.length !== numbers.length) {
                patch[field] = joinNumbers(kept);
            }
        }
    }
    return patch;
}

function recordPatch(
    record: PlanRecord,
    removed: Record<(typeof RECORD_REF_FIELDS)[number], Set<string>>
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const field of RECORD_REF_FIELDS) {
        const kept = withoutIds(record[field] ?? [], removed[field]);
        if (kept) {
            patch[field] = kept;
        }
    }
    return patch;
}

export function planDeletion(
    target: DeletionTarget,
    data: DeletionData
): DeletionPlan {
    const findingIds = target.kind === 'finding' ? [target.id] : [];
    const deletedFindingIds = new Set(findingIds);
    const planumIds =
        target.kind === 'finding'
            ? data.planums
                  .filter((planum) => planum.findingId === target.id)
                  .map((planum) => planum.id)
            : target.kind === 'planum'
              ? [target.id]
              : [];
    const profileIds =
        target.kind === 'finding'
            ? data.profiles
                  .filter((profile) => profile.findingId === target.id)
                  .map((profile) => profile.id)
            : target.kind === 'profile'
              ? [target.id]
              : [];
    const removedPlanumIds = new Set(planumIds);
    const removedProfileIds = new Set(profileIds);
    const removedNumbers = vanishingNumbers(data.findings, deletedFindingIds);

    const removals: ReferenceRemoval[] = [];
    const describe = (
        type: 'planum' | 'profile',
        descriptions: PlanDescription[],
        goingIds: Set<string>
    ) => {
        for (const description of descriptions) {
            if (goingIds.has(description.id)) {
                continue;
            }
            const patch = descriptionPatch(
                description,
                removedPlanumIds,
                removedProfileIds,
                removedNumbers
            );
            if (Object.keys(patch).length > 0) {
                removals.push({
                    type,
                    id: description.id,
                    projectId: description.projectId,
                    fields: Object.keys(patch),
                    patch
                });
            }
        }
    };
    describe('planum', data.planums, removedPlanumIds);
    describe('profile', data.profiles, removedProfileIds);

    const removedRecordRefs = {
        planumIds: removedPlanumIds,
        profileIds: removedProfileIds,
        findingIds: deletedFindingIds
    };
    for (const type of RECORD_TYPES) {
        for (const record of data.records[type]) {
            const patch = recordPatch(record, removedRecordRefs);
            if (Object.keys(patch).length > 0) {
                removals.push({
                    type,
                    id: record.id,
                    projectId: record.projectId,
                    fields: Object.keys(patch),
                    patch
                });
            }
        }
    }

    const goingOwners = new Set([
        ...findingIds.map((id) => `finding:${id}`),
        ...planumIds.map((id) => `findingPlanum:${id}`),
        ...profileIds.map((id) => `findingProfile:${id}`)
    ]);
    // Every description a picture could move to: all the family's that are live.
    const liveOwnerIds = new Set(
        [
            ...data.findings,
            ...data.planums,
            ...data.profiles,
            ...data.records.overallDescription
        ].map((owner) => owner.id)
    );
    const pictures = planOwnerRemoval(
        goingOwners,
        data.media,
        data.mediaLinks,
        liveOwnerIds
    );
    const deleted = new Set(pictures.deleteMediaIds);
    const media = data.media.filter((item) => deleted.has(item.id));

    return {
        findingIds,
        planumIds,
        profileIds,
        media,
        mediaMoves: pictures.moves,
        mediaLinkIds: pictures.deleteLinkIds,
        removals
    };
}

/**
 * The relation a changed description field belongs to, as its i18n suffix:
 * `inProfileWithPlanumIds` and `inProfileWithProfileIds` both read "Im Profil
 * mit".
 */
export function relationOfField(field: string): string {
    return field.replace(/(Planum|Profile)Ids$/, '');
}
